/* Copyright 2011-2019 The MathWorks, Inc. */

define([
    "dojo/aspect",
    "dojo/_base/declare",
    "dijit/_WidgetBase",
    "dijit/_TemplatedMixin",
    "dijit/registry",
    "dojo/text!./templates/Column.html",
    "./_WidgetMixin",
    "./_ChildrenWrapperMixin",
    "mw-form/mixins/ResizeMixin",
    "MW/toolstrip/hashes/listOfNewWidgets",
    "MW/toolstrip/constants/OrientationConstants"
], function (aspect, declare, _WidgetBase, _TemplatedMixin, registry, ColumnHtml, _WidgetMixin,
             _ChildrenWrapperMixin, ResizeMixin, listOfNewWidgets, OrientationConstants) {

    function matchesSelector (/*Element*/ element, /*String*/ selector) { // TODO: use domUtils.matchesSelector instead
        var matchesMethod;

        if (!element || !selector) {
            return false;
        }

        matchesMethod = 
            Element.prototype.matches || 
            Element.prototype.matchesSelector || 
            Element.prototype.mozMatchesSelector ||
            Element.prototype.msMatchesSelector || 
            Element.prototype.oMatchesSelector || 
            Element.prototype.webkitMatchesSelector ||
            function(s) {
                var matches = (this.document || this.ownerDocument).querySelectorAll(s),
                    i = matches.length;
                while (--i >= 0 && matches.item(i) !== this) {}
                return i > -1;            
            };

        return matchesMethod.call(element, selector);
    };

    var buttonsEligibleForSheddingLabels = ["PushButton", "DropDownButton", "SplitButton", "ToggleButton", "ToggleSplitButton", "ColorPickerButton", "GridPickerButton"];

    return declare([_WidgetBase, _TemplatedMixin, _WidgetMixin, _ChildrenWrapperMixin, ResizeMixin], {
        /** @lends Column# */

        /**
         * @class Column is a Container which can have Controls or Panels as it's children.
         * @extends dijit/_Container
         */

        templateString: ColumnHtml,
        shouldShedDescendantLabels: false,

        constructor: function () {
            var that = this;
            this._wrappers = {};

            this.own(
                aspect.after(this, "addChild", function (widget) {
                    var alignment = this.get("horizontalAlignment");
                    if (alignment === "left") {
                        alignment = "flex-start";
                    } else if (alignment === "right") {
                        alignment = "flex-end";
                    }
                    that._wrappers[widget.id].style.alignItems = alignment;
                    if (that.get("width")) {
                        //For new consolidated widgets, size mixin does not allow 100% as a value, so we have columnWidthMixin mixed into widgets like TextField.
                        //But some widgets do not grow with column (label, buttons etc)
                        //Only set width of 100% on children of column if they support 100%
                        if(widget._scaleToParentWidth) {
                            widget.set("width", that.get("width"));
                        }
                    }
                }, true)
            );
        },

        _setHorizontalAlignmentAttr: function (value) {
            var alignment = value;
            if (value === "left") {
                alignment = "flex-start";
            } else if (value === "right") {
                alignment = "flex-end";
            }
            
            Object.keys(this._wrappers).forEach(function (key) {
                this._wrappers[key].style.alignItems = alignment;
            }, this);
            this._set("horizontalAlignment", value);
        },

        _setWidthAttr: function (value) {
            if (value > 0) {
                this.domNode.style.width = value + "px";
                this._set("width", value);
                this.getChildren().forEach(function (widget) {
                   // g1740991: We need to account for widgets that support 100% when setting the width of a Column after construction
                    if(widget._scaleToParentWidth) {
                        widget.set("width", value);
                    }

                    // g2049549: Dynamically changing the column width should check and fix if the text is getting
                    // clipped for the widget.
                    if (widget._checkAndFixClippedText) {
                        widget._checkAndFixClippedText();
                    }
                });
            }
        },

        _setExtraColumnGapAttr: function (value) {
            this._set("extraColumnGap", value);
            if (value) {
                this.domNode.classList.add("extraColumnGap");
            } else {
               this.domNode.classList.remove("extraColumnGap");
            }
        },
        

        resize: function () {
            this.inherited(arguments);

            var children = this.getChildren();
            if (children) {
                for (var i = 0; i < children.length; i++) {
                    if (children[i].resize) {
                        children[i].resize();
                    } else {
                        children[i].set("height", children[i].get("height"));
                    }
                }
            }
            this.updateChildrenOrientations();
            // TODO: Update the 'showText' property of children, instead of forcing style using CSS
            this._updateDescendantTitle();
        },

        addChild: function (child) {
            this.inherited(arguments);
            this.updateChildrenOrientations();
            this.set("horizontalAlignment", this.get("horizontalAlignment"));
            // TODO: Update the 'showText' property of children, instead of forcing style using CSS
            this._updateDescendantTitle();
        },

        removeChild: function (child) {
            this.inherited(arguments);
            this.updateChildrenOrientations();
            this._updateDescendantTitle();
        },
        
        updateChildrenOrientations: function () {
            var parent = this.getParent();

            if (parent) {
                // g1745694: When the toolstrip width is reduced causing the section to collapse,
                // the parent of the widget would be "SectionPopup" instead of "Section". The
                // second clause checks this scenario.
                if (parent.type === "Section" || parent.type === "SectionPopup") {
                    this._updateChildrenOrientations();
                } else if (parent.type === "Panel") {
                    var grandParent = parent.getParent();
                    if (grandParent && grandParent.type === "Column") {
                        grandParent.updateChildrenOrientations();
                    }
                }
            }
        },

        // According to the Layout Policy of the Toolstrip:
        // If there is only one control in a column, it's orientation should be VERTICAL.
        // If there are more than one controls in a column, their orientation Ushould be HORIZONTAL.
        // The above rules also apply to nested controls in a panel.
        _updateChildrenOrientations: function () {
            var children = this.getChildren();

            if (children.length === 1) {
                // If there is only one child and that child is a Panel, then for each of the children
                // columns of the Panel, the controls within them need to be oriented according to
                // their number of siblings.
                if (children[0].type === "Panel") {
                    children[0].getChildren().forEach(function (column) {
                        var columnChildren = column.getChildren();
                        var newOrientation = columnChildren.length === 1 ? OrientationConstants.VERTICAL : OrientationConstants.HORIZONTAL;
                        columnChildren.forEach(function (control) {
                            if (control.get("orientation")) {
                                control.set("orientation", newOrientation);
                            }
                        });
                    });
                    // If there is only one child and that child is not a Panel, then the control's
                    // orientation can simply be set to Vertical.
                } else {
                    if (children[0].get('orientation') && children[0].get('orientation') !== OrientationConstants.VERTICAL) {
                        children[0].set("orientation", OrientationConstants.VERTICAL);
                    }
                }
            } else {
                // If there are more than 1 children then all the controls in the column, even
                // the nested ones need to have their orientation updated.
                // Note: The 0 children case is also taken care of here since forEach would be
                // ignored.
                if (this._isCompactDisplayMode()) {
                    this._updateMultipleChildrenOrientations(children, OrientationConstants.VERTICAL);
                } else {
                    this._updateMultipleChildrenOrientations(children, OrientationConstants.HORIZONTAL);
                }
            }
        },

        _updateMultipleChildrenOrientations: function (children, orientation) {
            children.forEach(function (child) {
                if (child.type === "Panel") {
                    child.getChildren().forEach(function (column) {
                        column.getChildren().forEach(function (control) {
                            if (control.get("orientation") && control.get("orientation") !== orientation) {
                                control.set("orientation", orientation);
                            }
                        });
                    });
                } else {
                    if (child.get("orientation") && child.get("orientation") !== orientation) {
                        child.set("orientation", orientation);
                    }
                }
            });
        },

        _isCompactDisplayMode: function () {
            // TODO: make this more robust.  We should likely have a local property.
            return this.domNode ? matchesSelector(this.domNode, '.compactToolstrip *') : null;
        },

        _setShouldShedDescendantLabelsAttr: function (shouldShed) {
            if (typeof shouldShed !== "boolean") {
                throw error("'shouldShedDescendantLabels' expects a boolean value.");
            }
            this._set("shouldShedDescendantLabels", shouldShed);
            
            this._updateDescendantTitle();
        },

        // when the button's text is hidden need to update the tooltip to show
        // text instead of description.
        _updateDescendantTitle: function () {
            var i, j, buttons;

            for (j = 0; j < buttonsEligibleForSheddingLabels.length; j++) {
                buttons = this.domNode.querySelectorAll("[data-type=" + buttonsEligibleForSheddingLabels[j] + "]");

                for (i = 0; i < buttons.length; ++i) {
                    var buttonWidget = registry.getEnclosingWidget(buttons[i]);

                    if (buttonWidget.orientation === "Horizontal") {
                        buttonWidget.set("showText", !this.shouldShedDescendantLabels);
                    }
                }
            }
        }
    });
});
